import { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Box, Chip, CircularProgress, TextField, Typography } from '@mui/material';
import { useInput, useRecordContext, type InputProps } from 'react-admin';

import {
    flattenAdminBlogTagTrees,
    getAdminBlogTagTrees,
    type AdminBlogRecord,
    type AdminBlogTagOption,
    type AdminBlogTagRecord,
} from '../admin/adminBlogDataProvider';

type BlogTagSelectorInputProps = InputProps & {
    helperText?: string;
};

function createFallbackOption(tag: AdminBlogTagRecord): AdminBlogTagOption {
    return {
        blogTagId: tag.blogTagId,
        blogTagTreeId: tag.blogTagTreeId,
        tagTreeName: tag.tagTreeName,
        parentBlogTagId: tag.parentBlogTagId,
        name: tag.name,
        slug: tag.slug,
        sortOrder: tag.sortOrder,
        pathLabel: `${tag.tagTreeName} / ${tag.name}`,
    };
}

export function BlogTagSelectorInput(props: BlogTagSelectorInputProps) {
    const { field, fieldState, id } = useInput({
        ...props,
        defaultValue: [],
    });
    const record = useRecordContext<AdminBlogRecord>();
    const [options, setOptions] = useState<AdminBlogTagOption[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let active = true;

        const loadOptions = async () => {
            setLoading(true);

            try {
                const trees = await getAdminBlogTagTrees();
                if (!active) {
                    return;
                }

                setOptions(flattenAdminBlogTagTrees(trees));
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };

        void loadOptions();

        return () => {
            active = false;
        };
    }, []);

    const selectedOptions = useMemo(() => {
        const selectedIds = Array.isArray(field.value) ? field.value.map((value) => String(value)) : [];
        const optionMap = new Map(options.map((option) => [option.blogTagId, option]));

        return selectedIds
            .map((tagId) => {
                const existingOption = optionMap.get(tagId);
                if (existingOption) {
                    return existingOption;
                }

                const fallbackTag = record?.tags?.find((tag) => tag.blogTagId === tagId);
                return fallbackTag ? createFallbackOption(fallbackTag) : null;
            })
            .filter((option): option is AdminBlogTagOption => Boolean(option));
    }, [field.value, options, record?.tags]);

    const helperMessage =
        fieldState.error?.message ?? props.helperText ?? 'Type to autosuggest tags from the configured blog tag trees.';

    return (
        <Autocomplete
            id={id}
            multiple
            options={options}
            value={selectedOptions}
            loading={loading}
            filterSelectedOptions
            groupBy={(option) => option.tagTreeName}
            getOptionLabel={(option) => option.pathLabel}
            isOptionEqualToValue={(option, value) => option.blogTagId === value.blogTagId}
            onChange={(_, value) => field.onChange(value.map((option) => option.blogTagId))}
            onBlur={field.onBlur}
            renderValue={(value, getTagProps) =>
                value.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });

                    return (
                        <Chip
                            key={key}
                            label={option.name}
                            size="small"
                            variant="outlined"
                            {...tagProps}
                        />
                    );
                })
            }
            renderOption={(optionProps, option) => {
                const { key, ...restOptionProps } = optionProps;

                return (
                    <Box component="li" key={key} {...restOptionProps}>
                        <Box>
                            <Typography sx={{ fontWeight: 700, color: '#0f172a' }}>{option.name}</Typography>
                            <Typography sx={{ color: '#64748b', fontSize: '0.82rem' }}>{option.pathLabel}</Typography>
                        </Box>
                    </Box>
                );
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={props.label ?? 'Tags'}
                    error={fieldState.invalid}
                    helperText={helperMessage}
                    fullWidth
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
}